"use client";

import React from 'react';
import { Icons } from '@/components/icons';
import { useState } from 'react'
import { determineTextColor } from '@/lib/utils';
import Image from 'next/image';
import { Avatar } from '@/components/avatar';

interface CardWithUserProps {
  title: string;
  description: string | null;
  url: string | null;
  hexColour: string;
  primaryBackgroundColour: string;
  cardStyle: string | null;
  profileImageUrl?: string | null;
  userName?: string | null;
  urlSlug: string | undefined;
}

const getCardStyle = (style: string | null) => {
  switch (style) {
    case 'plain':
      return 'bg-white rounded-lg drop-shadow-xl';
    case 'border':
      return 'bg-white rounded-lg border border-zinc-800';
    case 'shadow':
      return 'bg-white rounded-lg shadow-[5px_5px_0px_2px_#2E2E2E]';
    case 'transparent':
      return 'bg-white bg-opacity-20 rounded-lg shadow';
    default:
      return 'bg-white rounded-lg drop-shadow-xl';
  }
};

const CardWithUser: React.FC<CardWithUserProps> = ({ title, description, url, hexColour, primaryBackgroundColour, cardStyle, profileImageUrl, userName, urlSlug }) => {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const toggleDescription = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowFullDescription(!showFullDescription);
  };

  let textColour = ''
  if (cardStyle === 'transparent') {
    textColour = determineTextColor(primaryBackgroundColour);
  }

  const getDescription = () => {
    if (showFullDescription) {
      return description;
    } else if (description && description.length > 90) {
      return description.slice(0, 90) + '...';
    } else {
      return description;
    }
  };

  return (
    url ? (
      <a
        className={`flex flex-col ${getCardStyle(cardStyle)} p-4 mx-4 my-3 w-full md:w-1/2 lg:w-1/3 transition-transform duration-450 ease-in-out transform hover:scale-105`}
        href={url}
        target="_blank"
      >
        <div className="flex items-center">
            <div className="flex-grow">
            <h3 style={{ color: textColour }} className={`text-lg font-semibold mb-1 ml-2`}>{title}</h3>
                {description && (
                    <p style={{ color: textColour ? textColour : '#2D3748', whiteSpace: 'pre-line' }} className={`text-sm mb-2 ml-2 break-words`}>
                    {getDescription()}
                    {description && description.length > 90 && (
                    <div className="mt-2">
                        <span
                        style={{ color: hexColour }}
                        className="text-blue-500 underline font-semibold cursor-pointer"
                        onClick={toggleDescription}
                        >
                        {showFullDescription ? ' Read less' : ' Read more'}
                        </span>
                    </div>
                    )}
                </p>
                )}
            </div>
            <div>
                <Icons.chevronRight style={{ color: hexColour }} />
            </div>
        </div>
        { userName && (
            <div className="flex items-center space-x-3 mt-2 ml-2 border-t border-gray-200 pt-3">
                {profileImageUrl ? (
                    <div className="relative rounded-full overflow-hidden w-9 h-9">
                        <Image 
                            src={profileImageUrl} 
                            alt={userName || 'Profile image'} 
                            width={128} 
                            height={128} 
                            className="absolute inset-0 w-full h-full object-cover" 
                        />
                    </div>
                ) : (
                  <div className="relative rounded-full overflow-hidden w-9 h-9">
                    <Avatar name={userName || ''} size="xs" className="w-full h-full" colorise />
                  </div>
                )}
                <div>
                    <p className="text-xs text-gray-500">Hosted by</p>
                    <div className="flex flex-row">
                    <h3 className="text-sm font-semibold">{userName}</h3>
                    </div>
                </div>
            </div>
        )}
        <div>
        </div>
      </a>
    ) : (
      <a
        className={`flex flex-col ${getCardStyle(cardStyle)} p-4 mx-4 my-3 w-full md:w-1/2 lg:w-1/3 transition-transform duration-450 ease-in-out transform hover:scale-105`}
        href={urlSlug ? urlSlug : window.location.href}
        target="_blank"
      >
        <div className="flex items-center">
            <div className="flex-grow">
            <h3 style={{ color: textColour }} className={`text-lg font-semibold mb-1 ml-2`}>{title}</h3>
                {description && (
                    <p style={{ color: textColour ? textColour : '#2D3748', whiteSpace: 'pre-line' }} className={`text-sm mb-2 ml-2 break-words`}>
                    {getDescription()}
                    {description && description.length > 90 && (
                    <div className="mt-2">
                        <span
                        style={{ color: hexColour }}
                        className="text-blue-500 underline font-semibold cursor-pointer"
                        onClick={toggleDescription}
                        >
                        {showFullDescription ? ' Read less' : ' Read more'}
                        </span>
                    </div>
                    )}
                </p>
                )}
            </div>
            <div>
                <Icons.chevronRight style={{ color: hexColour }} />
            </div>
        </div>
        { userName && (
            <div className="flex items-center space-x-3 mt-2 ml-2 border-t border-gray-200 pt-3">
                {profileImageUrl ? (
                    <div className="relative rounded-full overflow-hidden w-9 h-9">
                        <Image 
                            src={profileImageUrl} 
                            alt={userName || 'Profile image'} 
                            width={128} 
                            height={128} 
                            className="absolute inset-0 w-full h-full object-cover" 
                        />
                    </div>
                ) : (
                    <div className="relative rounded-full overflow-hidden w-9 h-9">
                      <Avatar name={userName || ''} size="xs" className="w-full h-full" colorise />
                    </div>
                )}
                <div>
                    <p className="text-xs text-gray-500">Hosted by</p>
                    <div className="flex flex-row">
                    <h3 className="text-sm font-semibold">{userName}</h3>
                    </div>
                </div>
            </div>
        )}
        <div>
        </div>
      </a>
    )
  );
};

export default CardWithUser;